// IMG-1/IMG-2/IMG-3: decode (EXIF-oriented), content-hash, size guard, entity placement.
// ARC-3a: io imports core/doc only — the caller (composition root) owns cache + viewport.
import { newId } from "../doc/document";
import type { AssetRef, Doc, ImageEntity } from "../doc/entities";
import type { Command } from "../doc/commands";
import { sha256Hex } from "./projectFile";

const MAX_MEGAPIXELS = 100; // EC-IMG-7
const ASSUMED_DPI = 96; // IMG-3 initial size

export interface DecodedImage {
  bitmap: ImageBitmap;
  blob: Blob;
  hash: string;
  w: number;
  h: number;
}

export type DecodeOutcome =
  | { ok: true; image: DecodedImage }
  | { ok: false; error: string }
  | { ok: false; needsDownscaleConfirm: true; megapixels: number };

/**
 * Decode + hash a candidate image file. EXIF orientation is applied at decode; the
 * stored bytes stay original (EC-IMG-2). `allowHuge` bypasses the EC-IMG-7 guard.
 */
export async function decodeImageFile(file: Blob & { name?: string }, allowHuge = false): Promise<DecodeOutcome> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    // EC-IMG-3 (incl. HEIC on non-supporting browsers)
    return { ok: false, error: `Couldn't read ${file.name ?? "the image"} — try JPEG or PNG.` };
  }
  const megapixels = (bitmap.width * bitmap.height) / 1e6;
  if (megapixels > MAX_MEGAPIXELS && !allowHuge) {
    bitmap.close();
    return { ok: false, needsDownscaleConfirm: true, megapixels };
  }
  const bytes = new Uint8Array(await file.arrayBuffer());
  const hash = await sha256Hex(bytes);
  return {
    ok: true,
    image: { bitmap, blob: file, hash, w: bitmap.width, h: bitmap.height }
  };
}

/** EC-IMG-7 consented downscale: re-encode to PNG under the cap as a NEW asset. */
export async function downscaleImage(file: Blob): Promise<Blob | null> {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.sqrt((MAX_MEGAPIXELS * 1e6) / (bitmap.width * bitmap.height));
    const w = Math.floor(bitmap.width * scale);
    const h = Math.floor(bitmap.height * scale);
    const resized = await createImageBitmap(bitmap, { resizeWidth: w, resizeHeight: h, resizeQuality: "high" });
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(resized, 0, 0);
    return await canvas.convertToBlob({ type: "image/png" });
  } catch {
    return null;
  }
}

export interface PlacementView {
  centerWorld: { x: number; y: number };
  viewWmm: number; // viewport width in world mm
  viewHmm: number;
}

/**
 * IMG-3: build the addAsset+addEntities batch. Initial size = natural px at 96 dpi,
 * capped to fit 80% of the viewport; centered; `cascade` offsets multi-file drops (EC-IMG-4).
 */
export function buildImportCommand(
  doc: Doc,
  image: DecodedImage,
  layerId: string,
  view: PlacementView,
  cascade: number
): { cmd: Command; entityId: string } {
  const ref: AssetRef = {
    hash: image.hash,
    mime: image.blob.type || "image/png",
    bytes: image.blob.size,
    w: image.w,
    h: image.h
  };
  let wMm = (image.w * 25.4) / ASSUMED_DPI;
  let hMm = (image.h * 25.4) / ASSUMED_DPI;
  const cap = Math.min((view.viewWmm * 0.8) / wMm, (view.viewHmm * 0.8) / hMm);
  if (cap < 1) {
    wMm *= cap;
    hMm *= cap;
  }
  const entity: ImageEntity = {
    id: newId(),
    type: "image",
    layerId,
    locked: false,
    hidden: false,
    assetHash: image.hash,
    pos: {
      x: view.centerWorld.x - wMm / 2 + cascade * 10,
      y: view.centerWorld.y - hMm / 2 + cascade * 10
    },
    rotationDeg: 0,
    wMm,
    hMm,
    opacity: 1,
    grayscale: false,
    brightness: 0,
    contrast: 0
  };
  const commands: Command[] = [];
  if (!doc.assets[image.hash]) commands.push({ kind: "addAsset", hash: image.hash, ref }); // EC-IMG-1 dedupe
  commands.push({ kind: "addEntities", entities: [entity], z: [{ layerId, index: -1 }] });
  return { cmd: { kind: "batch", label: "import image", commands }, entityId: entity.id };
}
